import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, map, Observable, reduce, tap } from 'rxjs';
import { ApiService } from '../../core/api.service';
import { ApiResponse } from '../../models/api-response.model';
import { UserStore } from '../stores/user.store';
import { Program, ProgramDTO } from '../../models/programs.model';

@Injectable({ providedIn: 'root' })
export class ProgramsService {
  private readonly userStore = inject(UserStore);
  private readonly apiService = inject(ApiService);
  private readonly DEFAULT_LOCALE = 'en_US';

  private listPage(pageUrl: string): Observable<ApiResponse<ProgramDTO[]>> {
    return this.apiService.get<ApiResponse<ProgramDTO[]>>(pageUrl);
  }

  listAll(pageSize = 100): Observable<Program[]> {
    const firstUrl = `programs?pageSize=${pageSize}`;

    return this.listPage(firstUrl).pipe(
      expand((response) => {
        const next = response?.links?.nextPage ?? null;

        if (!next) return EMPTY;

        const nextPage = next.replace('v1/programs/projections?', 'programs?');

        return this.listPage(nextPage);
      }),

      map((response) => (response?.data ?? []).filter((dto) => dto.status !== 'DELETED').map((dto) => this.mapEntity(dto))),

      reduce((all, pageItems) => all.concat(pageItems), [] as Program[])
    );
  }

  private mapEntity = (entity: ProgramDTO): Program => {
    const locale = this.getCurrentLocale();
    const labels = entity.display_labels || {};

    const displayLabel =
      labels[locale] ??
      labels[this.DEFAULT_LOCALE] ?? // fallback to en_US
      Object.values(labels)[0] ??
      '';

    return {
      id: entity.id,
      timezone: entity.timezone,
      displayLabel,
    };
  };

  private getCurrentLocale(): string {
    const user = this.userStore.userSignal();
    if (!user) {
      return this.DEFAULT_LOCALE;
    }

    return user.preferences.defaultLocale;
  }
}
